import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Globe } from "lucide-react";

export default function InternationalTransferModal({ open, onClose, onSuccess }) {
  const { t, lang } = useApp();
  const [countries, setCountries] = useState([]);
  const [methods, setMethods] = useState([]);
  const [countryCode, setCountryCode] = useState("");
  const [methodId, setMethodId] = useState("");
  const [beneficiary, setBeneficiary] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/remittance/config").then((r) => {
        setCountries(r.data.countries);
        setMethods(r.data.methods);
      }).catch(() => {});
      setCountryCode(""); setMethodId(""); setBeneficiary(""); setAmount(""); setNote("");
    }
  }, [open]);

  const country = useMemo(() => countries.find((c) => c.code === countryCode), [countries, countryCode]);
  const method = useMemo(() => methods.find((m) => m.id === methodId), [methods, methodId]);
  const amt = parseFloat(amount) || 0;
  const fee = method?.fee || 0;
  const converted = country ? amt * country.rate : 0;
  const total = amt + fee;

  const submit = async () => {
    if (!countryCode || !methodId || !beneficiary.trim() || !amt) return;
    setLoading(true);
    try {
      await api.post("/wallet/international-transfer", {
        country_code: countryCode, method_id: methodId,
        beneficiary_name: beneficiary.trim(), amount: amt, note,
      });
      toast.success(t.toastIntlTransfer);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="international-transfer-modal" className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" /> {t.internationalTransfer}
          </DialogTitle>
          <DialogDescription>{t.intlTransferDesc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 pt-1">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.destinationCountry}</label>
            <Select value={countryCode} onValueChange={setCountryCode}>
              <SelectTrigger data-testid="intl-country-select" className="h-12 rounded-2xl mt-1.5">
                <SelectValue placeholder={t.destinationCountry} />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {countries.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`intl-country-${c.code}`}>
                    {lang === "ar" ? c.name_ar : c.name_en} · {c.currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.transferMethod}</label>
            <Select value={methodId} onValueChange={setMethodId}>
              <SelectTrigger data-testid="intl-method-select" className="h-12 rounded-2xl mt-1.5">
                <SelectValue placeholder={t.transferMethod} />
              </SelectTrigger>
              <SelectContent>
                {methods.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`intl-method-${m.id}`}>
                    {lang === "ar" ? m.name_ar : m.name_en} · {m.fee} {t.currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.beneficiaryName}</label>
            <Input data-testid="intl-beneficiary-input" value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)}
              placeholder={t.beneficiaryPlaceholder} className="h-12 rounded-2xl mt-1.5" />
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.amount}</label>
            <div className="relative mt-1.5">
              <Input data-testid="intl-amount-input" type="number" inputMode="decimal" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder={t.enterAmount}
                className="h-12 rounded-2xl text-lg font-bold tabular-nums" />
              <span className="absolute top-1/2 -translate-y-1/2 end-4 text-sm font-bold text-muted-foreground">{t.currency}</span>
            </div>
          </div>

          {country && amt > 0 && (
            <div data-testid="intl-summary" className="rounded-2xl bg-muted/50 border border-border p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t.recipientGets}</span>
                <span data-testid="intl-converted" className="font-bold tabular-nums text-primary">{formatSAR(converted, lang)} {country.currency}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{t.exchangeRate}</span>
                <span className="tabular-nums">1 {t.currency} = {country.rate} {country.currency}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t.transferFee}</span>
                <span className="font-semibold tabular-nums">{formatSAR(fee, lang)} {t.currency}</span>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-2">
                <span className="font-bold">{t.totalDebit}</span>
                <span data-testid="intl-total" className="font-black tabular-nums">{formatSAR(total, lang)} {t.currency}</span>
              </div>
            </div>
          )}

          <Button data-testid="intl-transfer-submit" onClick={submit}
            disabled={loading || !countryCode || !methodId || !beneficiary || !amount}
            className="w-full h-[52px] rounded-2xl text-base font-bold">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.sendAbroad}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
